import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogContent, DialogTitle, IconButton, InputAdornment, LinearProgress, Link, Skeleton, Tab, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, TextField, Tooltip, Typography
} from '@mui/material';
import {
  Close as CloseIcon, LocalPharmacyOutlined as PharmacyIcon, NearMeOutlined as DistanceIcon, PhoneOutlined as PhoneIcon,
  PlaceOutlined as PlaceIcon, ScheduleOutlined as HoursIcon, Search as SearchIcon, WarehouseOutlined as WarehouseIcon
} from '@mui/icons-material';
import { fmtMoney, plural } from '../../Documents/format';
import { BUCKET_COLORS } from '../../OnlineJournal/constants';
import { fmtInt, fmtQty } from '../../OnlineJournal/format';
import { useLoad } from '../../OnlineJournal/hooks/useLoad';
import { PharmacyProduct, RemainsMeta, SearchResponse, remainsApi } from '../remainsApi';
import { Origin, distanceKm, fmtDistance, hasCoords, pharmacyStocks } from '../remainsCalc';
import { BORDER, EMPTY_TEXT, emptySx, headCellSx, rowSx, smallChipSx, tabsSx } from '../remainsStyles';
import { MapLinks, OpenChip, RouteLink, StockExpiryCell, Value } from './RemainsCommon';

interface Props {
  /** Открытая аптека: null - окно закрыто */
  guid: string | null;
  data: SearchResponse | null;
  selected: string[];
  origin: Origin | null;
  meta: RemainsMeta;
  onClose: () => void;
}

type CardTab = 'found' | 'all';

interface Column { id: string; label: string; width: number; align?: 'right' | 'center'; tip?: string }

const PROFILE_HINT = 'заполняется в профиле аптеки (или "Пользователи")';

/** Ширины подобраны так, чтобы полный набор колонок помещался в окно без горизонтальной прокрутки */
function columnsFor(withPrices: boolean, withSums: boolean): Column[] {
  return [
    { id: 'name', label: 'Товар', width: 270 },
    { id: 'producer', label: 'Производитель', width: 145 },
    { id: 'mnn', label: 'МНН', width: 145 },
    { id: 'folder', label: 'Папка / тип 1С', width: 145 },
    { id: 'part', label: 'Делимость', width: 80, align: 'center' },
    { id: 'qty', label: 'Кол-во', width: 80, align: 'right' },
    ...(withPrices ? [{ id: 'price', label: 'Цена', width: 95, align: 'right' as const }] : []),
    ...(withSums ? [{ id: 'sum', label: 'Сумма', width: 105, align: 'right' as const }] : []),
    { id: 'expiry', label: 'Ближ. срок', width: 115, tip: 'По журналу сроков, без серий' }
  ];
}

function renderCell(id: string, p: PharmacyProduct): React.ReactNode {
  switch (id) {
    case 'name': {
      const ids = [p.sku ? `арт. ${p.sku}` : '', p.barcode ? `ШК ${p.barcode}` : ''].filter(Boolean).join(' · ');
      return (
        <Box sx={{ minWidth: 0 }}>
          <Typography
            title={p.name}
            sx={{ fontSize: 13, fontWeight: 600, color: '#0f172a', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
          >
            {p.name}
          </Typography>
          {ids && <Typography sx={{ fontSize: 11, color: '#94a3b8', fontFamily: 'monospace' }} noWrap title={ids}>{ids}</Typography>}
        </Box>
      );
    }
    case 'producer': return <Value text={p.producer} />;
    case 'mnn': return <Value text={p.mnn} />;
    case 'folder':
      return (
        <Box sx={{ minWidth: 0 }}>
          <Value text={p.folder} />
          {p.type && <Typography sx={{ fontSize: 11, color: '#94a3b8' }} noWrap title={p.type}>{p.type}</Typography>}
        </Box>
      );
    case 'part':
      return p.partCoeff
        ? <Chip size="small" label={`1/${p.partCoeff}`} sx={{ ...smallChipSx, color: '#0f766e', bgcolor: 'rgba(13,148,136,0.08)' }} />
        : <Typography sx={{ fontSize: 12.5, color: '#cbd5e1' }}>нет</Typography>;
    case 'qty': return <Typography sx={{ fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' }}>{fmtQty(p.qty)}</Typography>;
    case 'price': return <Typography sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>{fmtMoney(p.price, 2)}</Typography>;
    case 'sum': return <Typography sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>{fmtMoney(p.sum)}</Typography>;
    case 'expiry': return <StockExpiryCell expiry={p.expiry} />;
    default: return null;
  }
}

/** Товары аптеки: найденные (считаются на клиенте) или весь ассортимент (с сервера); при нехватке места сжимается и прокручивается сама */
const ProductsTable: React.FC<{ rows: PharmacyProduct[]; columns: Column[]; loading: boolean; empty: string }> = ({ rows, columns, loading, empty }) => {
  const minWidth = columns.reduce((acc, c) => acc + c.width, 0);
  return (
    <TableContainer sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, flex: '0 1 auto', minHeight: 200, overflow: 'auto' }}>
      <Table size="small" stickyHeader sx={{ minWidth, tableLayout: 'fixed', opacity: loading ? 0.6 : 1, transition: 'opacity .15s' }}>
        <TableHead>
          <TableRow>
            {columns.map(col => (
              <TableCell key={col.id} align={col.align} sx={{ ...headCellSx, width: col.width }}>
                {col.tip ? <Tooltip title={col.tip} arrow><span>{col.label}</span></Tooltip> : col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(p => (
            <TableRow key={p.zid} hover sx={rowSx}>
              {columns.map(col => (
                <TableCell key={col.id} align={col.align} sx={{ overflow: 'hidden' }}>{renderCell(col.id, p)}</TableCell>
              ))}
            </TableRow>
          ))}
          {!rows.length && (
            <TableRow>
              <TableCell colSpan={columns.length} align="center" sx={{ py: 5, color: '#64748b' }}>
                <Typography variant="body2">{empty}</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

/** Сведение об аптеке с иконкой: value - основное значение (null - «не указано» и подсказка, где заполнить), children - строка под ним */
const Info: React.FC<{ icon: React.ReactNode; label: string; value: React.ReactNode; hint?: string; grow?: number; children?: React.ReactNode }> = ({
  icon, label, value, hint, grow = 1, children
}) => (
  <Box sx={{ display: 'flex', gap: 1.25, minWidth: 0, flex: `${grow} 1 ${grow > 1 ? 320 : 180}px` }}>
    <Box sx={{ width: 32, height: 32, flexShrink: 0, borderRadius: 1.5, bgcolor: '#f1f5f9', color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', '& svg': { fontSize: 18 } }}>
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: '0.7rem', color: '#64748b', lineHeight: 1.4 }}>{label}</Typography>
      {value || (
        <>
          <Typography sx={emptySx}>{EMPTY_TEXT}</Typography>
          {hint && <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8' }}>{hint}</Typography>}
        </>
      )}
      {children}
    </Box>
  </Box>
);

const infoText = (text: string) => (
  <Typography sx={{ fontSize: '0.86rem', fontWeight: 600, color: '#0f172a', wordBreak: 'break-word', lineHeight: 1.4 }}>{text}</Typography>
);

const tabLabel = (text: string, count: number | null) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
    {text}
    {count !== null && (
      <Box component="span" sx={{ px: 0.75, borderRadius: 5, bgcolor: '#f1f5f9', color: '#475569', fontSize: 11.5, fontWeight: 700, lineHeight: '18px' }}>
        {fmtInt(count)}
      </Box>
    )}
  </Box>
);

/** Итоги вкладки: «По найденным: 12 позиций · 340 уп. · 1 250 000 ₸ · просрочено 5 уп.» */
const Totals: React.FC<{ label: string; positions: number; units: number; amount?: number | null; expired?: number }> = ({
  label, positions, units, amount, expired
}) => {
  const strong = (text: string) => <Box component="b" sx={{ color: '#0f172a' }}>{text}</Box>;
  return (
    <Typography sx={{ fontSize: '0.8rem', color: '#64748b', py: 0.75 }}>
      {label}: {strong(fmtInt(positions))} {plural(positions, 'позиция', 'позиции', 'позиций')} · {strong(fmtQty(units))} уп.
      {amount !== null && amount !== undefined && <> · {strong(fmtMoney(amount))}</>}
      {expired ? <> · <Box component="span" sx={{ color: BUCKET_COLORS.expired.color, fontWeight: 600 }}>просрочено {fmtQty(expired)} уп.</Box></> : null}
    </Typography>
  );
};

/** Карточка аптеки: контакты и режим работы из профиля, найденные товары и весь ассортимент по данным 1С */
const PharmacyDialog: React.FC<Props> = ({ guid, data, selected, origin, meta, onClose }) => {
  const [tab, setTab] = useState<CardTab>('found');
  const [draft, setDraft] = useState('');
  const [term, setTerm] = useState('');
  const [limit, setLimit] = useState(meta.limits.pharmacyProducts);
  const withPrices = meta.permissions.prices;
  // сумма - отдельное право, сервер отдаёт его только вместе с ценами
  const withSums = Boolean(meta.permissions.sums);

  // другая аптека - с найденных товаров и без поиска по ассортименту
  useEffect(() => {
    setTab('found');
    setDraft('');
    setTerm('');
    setLimit(meta.limits.pharmacyProducts);
  }, [guid, meta.limits.pharmacyProducts]);

  const pharmacy = useMemo(() => (data && guid ? data.pharmacies.find(p => p.guid === guid) || null : null), [data, guid]);
  const found = useMemo(() => (data && guid ? pharmacyStocks(data, guid, selected) : []), [data, guid, selected]);
  const all = useLoad(
    guid && tab === 'all' ? (silent: boolean, signal: AbortSignal) => remainsApi.pharmacy(guid, { term, limit }, silent, signal) : null,
    `${guid}|${term}|${limit}`, 0, 'Не удалось загрузить ассортимент аптеки'
  );

  if (!guid || !pharmacy) return null;

  const p = pharmacy;
  const columns = columnsFor(withPrices, withSums);
  const distance = origin && hasCoords(p) ? distanceKm(origin, p) : null;
  // ответ по прошлой аптеке не показываем, по этой же (новый поиск) - притушенным до прихода нового
  const assortment = all.data && all.data.pharmacy.guid === guid ? all.data : null;
  const foundSum = withSums ? found.reduce((acc, x) => acc + (x.sum || 0), 0) : null;
  const foundUnits = found.reduce((acc, x) => acc + x.qty, 0);
  const foundExpired = found.reduce((acc, x) => acc + (x.expiry ? x.expiry.expiredQty : 0), 0);
  const canMore = Boolean(assortment && assortment.truncated && limit < meta.limits.pharmacyProductsMax);
  const submit = () => setTerm(draft.trim());

  const isWarehouse = p.kind === 'warehouse';
  const kindLabel = isWarehouse ? 'Склад' : p.num ? `Аптека №${p.num}` : 'Аптека';
  // номер не повторяем, если он уже есть в названии («Аптека №910»)
  const caption = [
    p.name.toLowerCase().includes(kindLabel.toLowerCase()) ? '' : kindLabel,
    p.region || '',
    p.name1c && p.name1c !== p.name ? `в 1С: ${p.name1c}` : ''
  ].filter(Boolean).join(' · ');

  return (
    <Dialog
      open onClose={onClose} maxWidth={false}
      PaperProps={{ sx: { width: '100%', maxWidth: 1240, borderRadius: 3, boxShadow: '0 20px 48px rgba(15, 23, 42, 0.16)', overflow: 'hidden' } }}
    >
      <DialogTitle sx={{ py: 2, px: 2.5, display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: '#fff', borderBottom: `1px solid ${BORDER}` }}>
        <Box
          sx={{
            width: 44, height: 44, flexShrink: 0, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: isWarehouse ? '#7c3aed' : '#2563eb', bgcolor: isWarehouse ? 'rgba(124,58,237,0.08)' : 'rgba(37,99,235,0.08)'
          }}
        >
          {isWarehouse ? <WarehouseIcon /> : <PharmacyIcon />}
        </Box>
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.3 }}>{p.name}</Typography>
            <OpenChip pharmacy={p} />
          </Box>
          {caption && <Typography sx={{ fontSize: '0.78rem', color: '#64748b', mt: 0.25 }} noWrap title={caption}>{caption}</Typography>}
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="Закрыть" sx={{ color: 'text.secondary', alignSelf: 'flex-start' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 2.5, pb: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 2.5 }}>
          <Info icon={<PlaceIcon />} label="Адрес" value={p.address && infoText(p.address)} grow={2}>
            {hasCoords(p)
              ? <Box sx={{ mt: 0.25 }}><MapLinks point={p} /></Box>
              : <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8' }}>нет точки на карте</Typography>}
          </Info>
          <Info icon={<HoursIcon />} label="Время работы" value={p.hours && infoText(p.hours)} hint={PROFILE_HINT} />
          {meta.permissions.contacts && (
            <Info
              icon={<PhoneIcon />} label="Рабочий телефон" hint={PROFILE_HINT}
              value={p.phone && (
                <Link href={`tel:${p.phone.replace(/[^\d+]/g, '')}`} underline="hover" sx={{ fontSize: '0.86rem', fontWeight: 600, fontFamily: 'monospace' }}>
                  {p.phone}
                </Link>
              )}
            />
          )}
          {origin && (
            <Info
              icon={<DistanceIcon />} label="Расстояние по прямой"
              value={distance !== null && hasCoords(p) ? (
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  {infoText(fmtDistance(distance))}
                  <RouteLink from={origin} to={p} label="Маршрут" sx={{ fontSize: 12 }} />
                </Box>
              ) : <Typography sx={emptySx}>нет координат аптеки</Typography>}
            >
              <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8' }} noWrap title={origin.label}>от «{origin.label}»</Typography>
            </Info>
          )}
        </Box>

        <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', columnGap: 2, flexWrap: 'wrap', borderBottom: `1px solid ${BORDER}` }}>
          <Tabs value={tab} onChange={(_, v: CardTab) => setTab(v)} sx={tabsSx}>
            <Tab value="found" label={tabLabel(selected.length ? 'Выбранные товары' : 'Найденные товары', found.length)} />
            <Tab value="all" label={tabLabel('Весь ассортимент', assortment ? assortment.pharmacy.positions : null)} />
          </Tabs>
          <Box sx={{ flexGrow: 1 }} />
          {tab === 'found' && (
            <Totals label={selected.length ? 'По выбранным' : 'По найденным'} positions={found.length} units={foundUnits} amount={foundSum} expired={foundExpired} />
          )}
          {tab === 'all' && assortment && (
            <Totals label="Всего в аптеке" positions={assortment.pharmacy.positions} units={assortment.pharmacy.units} amount={assortment.pharmacy.amount} />
          )}
        </Box>

        {tab === 'found' && <ProductsTable rows={found} columns={columns} loading={false} empty="Найденных товаров в этой аптеке нет" />}

        {tab === 'all' && (
          <>
            <TextField
              size="small" placeholder="Поиск по ассортименту аптеки" value={draft}
              onChange={e => setDraft(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') submit(); }}
              inputProps={{ maxLength: 100 }} sx={{ width: 340, maxWidth: '100%', flexShrink: 0 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} /></InputAdornment> }}
            />

            {all.error && (
              <Alert
                severity={all.error.forbidden ? 'warning' : 'error'}
                action={all.error.forbidden ? undefined : <Button color="inherit" size="small" onClick={all.retry}>Повторить</Button>}
              >
                {all.error.text}
              </Alert>
            )}

            {assortment && assortment.warnings.map(w => <Alert key={w} severity="warning" sx={{ py: 0.25 }}>{w}</Alert>)}

            {assortment && assortment.truncated && (
              <Alert
                severity="info" sx={{ py: 0.25 }}
                action={canMore ? (
                  <Button color="inherit" size="small" onClick={() => setLimit(meta.limits.pharmacyProductsMax)}>
                    Показать до {fmtInt(meta.limits.pharmacyProductsMax)}
                  </Button>
                ) : undefined}
              >
                Показаны первые {fmtInt(assortment.limit)} товаров{term ? ' по запросу' : ''}{canMore ? '' : ' - уточните поиск'}.
              </Alert>
            )}

            <Box sx={{ height: 3, flexShrink: 0, mb: -1.5 }}>{all.loading && assortment && <LinearProgress sx={{ height: 3 }} />}</Box>
            {assortment ? (
              <ProductsTable
                rows={assortment.products} columns={columns} loading={all.loading}
                empty={term ? `По запросу «${term}» в аптеке ничего нет` : 'В данных 1С у аптеки нет остатков'}
              />
            ) : !all.error && <Skeleton variant="rounded" height={260} sx={{ borderRadius: 2, flexShrink: 0 }} />}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default React.memo(PharmacyDialog);
